import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateDrawForm = ({ onDrawCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    draw_date: '',
    prize_description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/draws`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Draw created successfully!');
      setFormData({
        title: '',
        description: '',
        draw_date: '',
        prize_description: ''
      });
      onDrawCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create draw');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-2xl rounded-2xl border border-white/[0.06] bg-[#18181b] p-5 sm:rounded-3xl sm:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-[#EAB308]/20 p-3 rounded-xl">
          <Gift className="w-6 h-6 text-[#EAB308]" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold text-white sm:text-xl">Create new draw</h2>
          <p className="text-[#a1a1aa] text-sm">Set up a new giveaway</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title" className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Draw Title</Label>
          <Input
            id="title"
            data-testid="draw-title-input"
            placeholder="e.g., iPhone 15 Pro Giveaway"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white placeholder:text-[#52525b] focus-visible:ring-[#EAB308]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description" className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Description</Label>
          <Textarea
            id="description"
            data-testid="draw-description-input"
            placeholder="Describe the giveaway..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
            className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white placeholder:text-[#52525b] focus-visible:ring-[#EAB308]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="prize" className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Prize Description</Label>
          <Input
            id="prize"
            data-testid="draw-prize-input"
            placeholder="e.g., iPhone 15 Pro 256GB"
            value={formData.prize_description}
            onChange={(e) => setFormData({ ...formData, prize_description: e.target.value })}
            required
            className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white placeholder:text-[#52525b] focus-visible:ring-[#EAB308]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="date" className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Draw Date</Label>
          <Input
            id="date"
            data-testid="draw-date-input"
            type="datetime-local"
            value={formData.draw_date}
            onChange={(e) => setFormData({ ...formData, draw_date: e.target.value })}
            required
            className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white focus-visible:ring-[#EAB308]"
          />
        </div>

        <Button
          type="submit"
          data-testid="create-draw-submit-btn"
          disabled={loading}
          className="mt-6 h-12 w-full rounded-full bg-[#EAB308] font-semibold text-black hover:bg-[#CA8A04] active:scale-[0.98]"
        >
          {loading ? 'Creating...' : 'Create Draw'}
        </Button>
      </form>
    </motion.div>
  );
};

export default CreateDrawForm;
